import type { CalculatorInputValues } from '../calculators/types';
import {
  buildBreakEvenScenarios,
  calculateBreakEvenDecision,
  describeBreakEvenDecision,
  type BreakEvenDecisionInput,
} from '../calculators/breakEvenDecision';
import {
  DecisionCardGrid,
  DecisionComparisonTable,
  DecisionInsight,
} from './DecisionSupport';

const currencyFormatter = new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 });
const numberFormatter = new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 });

function numberValue(values: CalculatorInputValues, key: string): number {
  const value = values[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function optionalNumber(values: CalculatorInputValues, key: string): number | undefined {
  const value = values[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function toBreakEvenInput(values: CalculatorInputValues): BreakEvenDecisionInput {
  return {
    fixedCost: numberValue(values, 'fixedCost'),
    unitPrice: numberValue(values, 'unitPrice'),
    unitVariableCost: numberValue(values, 'unitVariableCost'),
    targetProfit: optionalNumber(values, 'targetProfit'),
    currentUnits: optionalNumber(values, 'currentUnits'),
  };
}

function won(value: number | null): string {
  return value === null ? '계산 불가' : `${currencyFormatter.format(Math.round(value))}원`;
}

function units(value: number | null): string {
  return value === null ? '계산 불가' : `${currencyFormatter.format(Math.round(value))}개`;
}

function percent(value: number | null): string {
  return value === null ? '계산 불가' : `${numberFormatter.format(value)}%`;
}

export function BreakEvenDecisionPanel({ values }: { values: CalculatorInputValues }) {
  const input = toBreakEvenInput(values);
  const result = calculateBreakEvenDecision(input);
  const hasTargetProfit = typeof input.targetProfit === 'number' && input.targetProfit > 0;
  const hasCurrentUnits = typeof input.currentUnits === 'number';

  const cards = [
    {
      label: '목표 이익 달성 판매량',
      value: hasTargetProfit ? units(result.targetUnits) : '목표 이익 미입력',
      detail: hasTargetProfit
        ? `목표 이익 ${won(result.targetProfit)}을 달성하기 위한 최소 판매량입니다.`
        : '목표 이익을 입력하면 손익분기점을 넘어 필요한 판매량을 계산합니다.',
    },
    {
      label: '목표 이익 달성 매출',
      value: hasTargetProfit ? won(result.targetRevenue) : '목표 이익 미입력',
      detail: hasTargetProfit
        ? '현재 판매단가가 유지된다는 가정의 최소 매출입니다.'
        : '목표 이익 입력 시 필요한 매출을 함께 계산합니다.',
    },
    {
      label: '현재 판매량 예상 손익',
      value: hasCurrentUnits ? won(result.currentProfit) : '현재 판매량 미입력',
      detail: hasCurrentUnits
        ? `현재 판매량 ${units(result.currentUnits)} 기준 추정 영업손익입니다.`
        : '현재 판매량을 입력하면 손익분기점 대비 실제 위치를 확인할 수 있습니다.',
    },
    {
      label: '손익분기 여유율',
      value: hasCurrentUnits ? percent(result.safetyRate) : '현재 판매량 미입력',
      detail: hasCurrentUnits && result.safetyUnits !== null
        ? result.safetyUnits >= 0
          ? `손익분기 판매량보다 ${units(result.safetyUnits)} 여유가 있습니다.`
          : `손익분기 판매량까지 ${units(Math.abs(result.safetyUnits))} 부족합니다.`
        : '현재 판매량과 손익분기 판매량의 차이를 비율로 표시합니다.',
    },
  ];

  const scenarioColumns = ['단위당 공헌이익', '손익분기 판매량', '손익분기 매출'];
  if (hasTargetProfit) scenarioColumns.push('목표이익 판매량');

  const scenarios = buildBreakEvenScenarios(input).map((scenario) => ({
    id: scenario.id,
    label: scenario.label,
    description: scenario.description,
    values: [
      won(scenario.result.unitContribution),
      units(scenario.result.breakEvenUnits),
      won(scenario.result.breakEvenRevenue),
      ...(hasTargetProfit ? [units(scenario.result.targetUnits)] : []),
    ],
  }));

  return (
    <section className="decision-support" aria-labelledby="break-even-decision-title">
      <div className="decision-support-heading">
        <p className="eyebrow">DECISION SUPPORT</p>
        <h2 id="break-even-decision-title">손익분기·목표이익 의사결정</h2>
        <p>단순 BEP를 넘어 목표이익에 필요한 판매량과 현재 손익 위치, 가격·비용 변화의 영향을 비교합니다.</p>
      </div>

      <DecisionInsight title="현재 손익 구조 해석">
        <p>{describeBreakEvenDecision(result)}</p>
        <p className="decision-caveat">
          이 계산은 입력한 고정비와 단위당 변동비를 기준으로 한 단순 손익 모델입니다. 세금, 재고손실, 환불, 단계별 수수료처럼 판매량에 따라 달라지는 비용은 별도로 반영해야 합니다.
        </p>
      </DecisionInsight>

      <DecisionCardGrid
        title="목표와 현재 위치"
        description="선택 입력을 추가하면 손익분기점 이후의 목표와 현재 실적을 함께 해석할 수 있습니다."
        items={cards}
      />

      <DecisionComparisonTable
        title="조건 변화 비교"
        description="판매단가 인하, 변동비 상승, 고정비 상승이 손익분기점에 미치는 영향을 같은 기준으로 비교합니다."
        columns={scenarioColumns}
        rows={scenarios}
      />
    </section>
  );
}
